import React, { useState } from "react";
import { Card, Button, Table, Typography, Row, Col } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { IDeleteRequestState } from "../../pages/Board/types";
import ReConfirmModal from "components/ReConfirmModal";

const { Title, Text } = Typography;

/**
 * DeleteConfirmation component displays details of a deletion request and allows the user to approve or reject it.
 *
 * Features:
 * - Shows space, domain, reason, and creation time for the deletion request.
 * - Allows toggling the display of file details.
 * - Provides Approve and Reject buttons, which are enabled only if the user has access.
 *
 * @component
 * @param {Object} props
 * @param {IDeleteRequest} props.request - The deletion request data to display.
 * @param {() => void} props.onApprove - Callback when the Approve button is clicked.
 * @param {() => void} props.onReject - Callback when the Reject button is clicked.
 * @param {boolean} [props.haveAccess=false] - Whether the current user has permission to approve/reject.
 */
const DeleteConfirmation: React.FC<{ request: IDeleteRequestState; onApprove: () => void; onReject: () => void; haveAccess: boolean }> = ({ request, onApprove, onReject, haveAccess = false }) => {
  const [showFiles, setShowFiles] = useState(false);
  const [rcmodal, setrcmodal] = useState(false);
    const [rcState, setrcState] = useState("");

    const handleApprove = () => {
        setrcmodal(true);
        setrcState("Approve");
    };

    const handleReject = () => {
        setrcmodal(true);
        setrcState("Reject");
    };
    const handleAccept = () => {
        setrcmodal(false);
        if (rcState == "Approve") {
            onApprove();
        } else if (rcState == "Reject") {
            onReject();
        }
    };
    const handleCancel = () => {
        setrcmodal(false);
    };

  const columns = [
    {
      title: "File Details",
      dataIndex: "file",
      key: "file",
    },
  ];

  const dataSource = request?.selected_prefixes?.map((file, index) => ({
    key: index,
    file,
  }));

  return (
    <Card title={<Title level={4}>Delete Confirmation</Title>} bordered={true}>
      <Row gutter={16} style={{ marginBottom: "16px" }}>
        <Col xs={24} sm={8}>
          <Text strong>Space name:</Text> {request.space_name || "-"}
        </Col>
        <Col xs={24} sm={8}>
          <Text strong>Domain:</Text> {request.domain_name || "-"}
        </Col>
        <Col xs={24} sm={8}>
          <Text strong>Region:</Text> {request.region_name || "-"}
        </Col>
      </Row>
      <Row style={{ marginBottom: "16px" }}>
        <Col xs={24}>
          <Text strong>Reason:</Text> {request.reason_for_deletion || "-"}
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "16px" }}>
        <Col xs={24} sm={12}>
          <Text strong>Created By:</Text> {request.loggedInUserEmail || "-"}
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Created At:</Text> {request.deletionRequestedTime || "-"}
        </Col>
      </Row>
      <Row style={{ marginBottom: "16px" }}>
        <Col xs={24}>
          <Text strong>Status:</Text> {request.status || "-"}
        </Col>
      </Row>
      <div style={{ marginTop: "16px" }}>
        {
          request?.status?.toLocaleLowerCase() === "pending" && <>  
            <Button type="primary" onClick={handleApprove} style={{ marginRight: "8px" }} disabled={!haveAccess}>
              Approve
            </Button>
            <Button onClick={handleReject} disabled={!haveAccess}>
              Reject
            </Button>
          </>
        }
      </div>
      {Array.isArray(request?.selected_prefixes) && request.selected_prefixes.length > 0 && <div style={{ marginTop: "16px", cursor: "pointer", display: "flex", alignItems: "center" }} onClick={() => setShowFiles(!showFiles)}>
        {showFiles ? <DownOutlined /> : <RightOutlined />}
        <Text strong style={{ marginLeft: 8 }}>
          File Details ({request?.selected_prefixes.length} files)
        </Text>
      </div>}
      {showFiles && (
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          style={{ marginTop: "16px" }}
          scroll={{ y: 200 }}
        />
      )}
       <ReConfirmModal
          useCase={rcState}
          visible={rcmodal}
          onCancel={handleCancel}
          onConfirm={handleAccept}
            />
    </Card>
  );
};

export default DeleteConfirmation;
